import {
  ShoppingCart,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Package,
  Calendar,
  Search,
} from "lucide-react";

import { useState, useEffect } from "react";
import { useOrder } from "../context/OrderContext";

const OrdersList = ({ orders, updateOrderStatus, getStatusColor }) => {
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("active");

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <AlertCircle className="w-3 h-3" />;
      case "preparing":
        return <Clock className="w-3 h-3" />;
      case "ready":
        return <Package className="w-3 h-3" />;
      case "completed":
        return <CheckCircle className="w-3 h-3" />;
      default:
        return <AlertCircle className="w-3 h-3" />;
    }
  };

  const getPriorityLevel = (createdAt) => {
    const now = new Date();
    const orderTime = new Date(createdAt);
    const timeDiff = (now - orderTime) / (1000 * 60); // in minutes

    if (timeDiff > 30) return "high";
    if (timeDiff > 15) return "medium";
    return "low";
  };

  const getStatusColors = (status) => {
    switch (status) {
      case "pending":
        return "bg-gray-600 text-white";
      case "preparing":
        return "bg-gray-700 text-white";
      case "ready":
        return "bg-green-600 text-white";
      case "completed":
        return "bg-black text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  // Filter orders by order ID and status
  const filteredOrders = orders.filter((order) => {
    const matchesSearch = search.trim()
      ? String(order.id).toLowerCase().includes(search.trim().toLowerCase())
      : true;

    const matchesStatus = (() => {
      switch (viewMode) {
        case "completed":
          return order.status === "completed";
        case "cancelled":
          return order.status === "cancelled";
        case "active":
        default:
          return !["completed", "cancelled"].includes(order.status);
      }
    })();

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-black rounded-lg">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-black">
                  Order Management
                </h1>
                <p className="text-sm text-gray-600">{orders.length} orders</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-700">
              <Calendar className="w-4 h-4" />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Items Summary */}
      {orders.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-300 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
            <h2 className="text-lg font-semibold text-black">
              Popular Items Today
            </h2>
            <div className="flex gap-3 items-center">
              <div className="flex rounded-lg overflow-hidden border border-gray-300">
                <button
                  onClick={() => setViewMode("active")}
                  className={`px-4 py-2 text-sm font-medium transition-all ${
                    viewMode === "active"
                      ? "bg-black text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Active Orders
                </button>
                <button
                  onClick={() => setViewMode("completed")}
                  className={`px-4 py-2 text-sm font-medium transition-all border-l border-gray-300 ${
                    viewMode === "completed"
                      ? "bg-black text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setViewMode("cancelled")}
                  className={`px-4 py-2 text-sm font-medium transition-all border-l border-gray-300 ${
                    viewMode === "cancelled"
                      ? "bg-black text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Cancelled
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by Order ID..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm transition-all"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => {
              // Aggregate items only from active orders (not completed or cancelled)
              const itemCounts = {};
              orders
                .filter(
                  (order) => !["completed", "cancelled"].includes(order.status)
                )
                .forEach((order) => {
                  (order.items || []).forEach((item) => {
                    if (itemCounts[item.name]) {
                      itemCounts[item.name] += item.quantity;
                    } else {
                      itemCounts[item.name] = item.quantity;
                    }
                  });
                });

              // Sort by quantity and show top items
              const sortedItems = Object.entries(itemCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 8); // Show top 8 items

              // If no active orders, show a message
              if (sortedItems.length === 0) {
                return (
                  <p className="text-sm text-gray-600">
                    No active orders with items
                  </p>
                );
              }

              return sortedItems.map(([itemName, totalQuantity]) => (
                <div
                  key={itemName}
                  className="flex items-center space-x-2 bg-gray-200 px-3 py-2 rounded-full border border-gray-300"
                >
                  <Package className="w-4 h-4 text-gray-700" />
                  <span className="text-sm font-medium text-black">
                    {itemName}
                  </span>
                  <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full font-semibold">
                    {totalQuantity}
                  </span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-black mb-2">
              No orders yet
            </h3>
            <p className="text-gray-600">
              Orders will appear here when customers place them
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-stretch">
            {filteredOrders.map((order, index) => {
              const priority = getPriorityLevel(order.createdAt);
              const priorityColors = {
                high: "border-gray-400 bg-gray-50/60",
                medium: "border-gray-300 bg-gray-50/40",
                low: "border-gray-200 bg-white",
              };

              return (
                <div
                  key={order.id}
                  className={`relative flex flex-col h-full overflow-hidden rounded-xl border-2 ${priorityColors[priority]} transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 group`}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animation: "slideUp 0.4s ease-out forwards",
                  }}
                >
                  {/* Priority indicator */}
                  {priority === "high" && (
                    <div className="absolute top-3 right-3 w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
                  )}

                  <div className="flex flex-col h-full p-4">
                    {/* Order header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-black text-sm truncate">
                            #{String(order.id).slice(-6)}
                          </h3>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(order.status)}
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColors(
                                order.status
                              )}`}
                            >
                              {order.status}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center text-xs text-gray-700 mt-1">
                          <Users className="w-3 h-3 mr-1" />
                          <span className="truncate">{order.customerName}</span>
                          <span className="mx-1">•</span>
                          <span>Table {order.tableNumber}</span>
                        </div>
                      </div>
                    </div>

                    {/* Order total */}
                    <div className="text-right mb-3">
                      <span className="text-lg font-bold text-black">
                        ${order.total?.toFixed(2) || "0.00"}
                      </span>
                    </div>

                    {/* Order items preview */}
                    <div className="mb-3">
                      <div className="space-y-2">
                        {(order.items || []).map((item, itemIndex) => (
                          <div key={itemIndex} className="text-xs">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700 flex-1">
                                {item.quantity}× {item.name}
                              </span>
                              <span className="font-medium text-black ml-2">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                            {item.specialInstructions && (
                              <div className="mt-1 text-xs text-red-600 pl-4">
                                Note: {item.specialInstructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Time info */}
                    <div className="text-xs text-gray-600 mb-3 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(order.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>

                    {/* Action buttons */}
                    <div className="space-y-2 mt-auto">
                      {order.status === "pending" && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              updateOrderStatus(order.id, "preparing")
                            }
                            className="flex-1 px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-lg shadow-sm border border-yellow-300 transition-colors duration-150 flex items-center justify-center"
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            Start
                          </button>
                          <button
                            onClick={() =>
                              updateOrderStatus(order.id, "cancelled")
                            }
                            className="px-3 py-2 bg-gray-500 text-white text-xs font-bold rounded-lg shadow-sm  transition-colors duration-150"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      {order.status === "preparing" && (
                        <button
                          onClick={() => updateOrderStatus(order.id, "ready")}
                          className="w-full px-3 py-2 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white text-xs font-bold rounded-lg shadow-sm border border-green-400 transition-colors duration-150 flex items-center justify-center"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark Ready
                        </button>
                      )}
                      {order.status === "ready" && (
                        <button
                          onClick={() =>
                            updateOrderStatus(order.id, "completed")
                          }
                          className="w-full px-3 py-2 bg-gradient-to-r from-gray-800 to-black hover:from-gray-900 hover:to-black text-white text-xs font-bold rounded-lg shadow-sm border border-gray-700 transition-colors duration-150"
                        >
                          Complete
                        </button>
                      )}
                    </div>

                    {/* Order Summary */}
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-gray-600 text-sm font-medium">
                        Total Items:{" "}
                        {(order.items || []).reduce(
                          (sum, item) => sum + item.quantity,
                          0
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default OrdersList;
